import useLocalStorage from '@rehooks/local-storage';
import { useQuery } from 'react-query';
import {
  AppsV1NavigationMenuItem,
  AppsV1NavigationResponse,
  AppsV1NavigationSuccessResponse,
  AppsV1NavigationUniveralIdentityResponse,
  Entity,
} from '../types';
import { fetchWrapper, formatLocalStorageKey } from '../utils';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';
import { useEffect } from 'react';

let hasNavCacheBeenInvalidated = false;
let hasNavDataBeenFetched = false;

/**
 * Adds provided menu item before the given menu item id, if it exists
 */

export type NavigationInsertionItem = {
  targetId: string;
  menuItem: AppsV1NavigationMenuItem;
  enabled: boolean;
};

export const insertNavigationItems = ({
  menus,
  itemsToInsert,
}: {
  menus: AppsV1NavigationSuccessResponse['menus'];
  itemsToInsert: NavigationInsertionItem[];
}): AppsV1NavigationSuccessResponse['menus'] => {
  const updatedMenus = [...menus];

  itemsToInsert.forEach(({ targetId, menuItem, enabled }) => {
    if (!enabled) return;

    const index = updatedMenus.findIndex(({ id }) => id === targetId);
    const newItemExists = updatedMenus.some(({ id }) => id === menuItem.id);

    if (index !== -1 && !newItemExists) {
      updatedMenus.splice(index + 1, 0, menuItem);
    }
  });

  return updatedMenus;
};

export const useBillingNavigation = (
  { enabled }: { enabled: boolean } = { enabled: true },
) => {
  //   const [data, setData] = useState<AppsV1NavigationResponse | null>(null);

  //   useEffect(() => {
  //     const fetchData = async () => {
  //       try {
  //         const data = await fetchWrapper<AppsV1NavigationResponse>(
  //           NAVIGATION_ENDPOINTS.V1_NAVIGATION,
  //         );
  //         setData(data);
  //       } catch (err) {
  //         console.error('Error fetching navigation data:', err);
  //       }
  //     };

  //     fetchData();
  //   }, []);

  //   return { data };
  // }
  const [cachedNavData, setCachedNavData] =
    useLocalStorage<AppsV1NavigationSuccessResponse>(
      formatLocalStorageKey('ZNavMenu'),
    );

  const zNavMenuQuery = useQuery<AppsV1NavigationSuccessResponse>(
    'ZNavMenu',
    async () => {
      const [navigation, universalId] = await Promise.all([
        /* For left-hand nav menu, settings menu, and
         * accessible & inaccessible entities (if multientity is enabled)
         */
        fetchWrapper<AppsV1NavigationResponse>(
          NAVIGATION_ENDPOINTS.V1_NAVIGATION,
        ),
        /* For universal identity entity list ("Universal ID")
         * this list, if present, takes precedent over multientity
         */
        fetchWrapper<AppsV1NavigationUniveralIdentityResponse>(
          NAVIGATION_ENDPOINTS.V1_UNIVERSAL_IDENTITY,
        ),
      ]);

      if (!navigation?.success) {
        const requestId = navigation?.requestId;
        throw new Error(
          `Failed to retrieve navigation ${
            requestId ? `(requestId: ${requestId})` : ''
          }`,
        );
      }

      const universalEntities = universalId?.entities?.flat(Infinity) as
        | Entity[]
        | undefined;
      const result = {
        ...navigation,
        // Empty array is truthy — only prefer universal entities when non-empty
        entities: universalEntities?.length
          ? universalEntities
          : navigation?.entities,
      };

      return result;
    },
    {
      initialData: () => {
        if (cachedNavData) {
          return cachedNavData;
        }
      },
      onSuccess: (data) => {
        setCachedNavData(data);
      },
      enabled: false,
      refetchOnMount: false,
      staleTime: 0,
    },
  );

  // Query to invalidate navigation data
  const { refetch: invalidateNavCache } = useQuery(
    'SingleNavData',
    async () => {
      window
        .fetch('/apps/api/navigation/fetch', { method: 'POST' })
        .then(function (response) {
          if (response.status === 200) {
            console.log('Navigation cache invalidated');
            // Once the nav cache is refreshed, re-fetch the navigation data
            zNavMenuQuery.refetch();
          }
        })
        .catch(function (error) {
          console.error(error);
        });
    },
    {
      enabled: false,
      onSuccess: async () => {},
    },
  );

  /*
   * Invalidate the nav data when the component mounts.
   *
   * Even though this will run for each component that invokes this hook,
   * the caching nature of react-query will prevent the fetch from happening
   * more than once, which is fine because we only need to invalidate the cache
   * once (e.g. the first time the query runs) per page load.
   */
  useEffect(() => {
    if (enabled) {
      const isHomePage = window.location.pathname === '/platform/webapp';
      if (isHomePage && !hasNavCacheBeenInvalidated) {
        hasNavCacheBeenInvalidated = true;
        invalidateNavCache();
      } else if (!hasNavDataBeenFetched) {
        hasNavDataBeenFetched = true;
        /*
            zNavMenuQuery must be manually called due to the fact that when this hook is called, the query is ran automatically
            and the refetch that gets triggered by invalidateNavCache() is not ran because the query is already in a loading state.
  
            So the solution is to manually trigger the nav menu query after the invalidateNavCache() query is ran, or here if not on the homepage.
         */
        zNavMenuQuery.refetch();
      }
    }
  }, [enabled]); // Dependency array ensures query only runs once

  return zNavMenuQuery;
};
